import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { useTranslationApp } from "../../lib/i18next";
import { Trans } from "react-i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { off } from "firebase/database";
import {
  BUTTON_UPGRADE_PLAN_2,
  BUTTON_UPGRADE_TO_PAID_PLAN,
  REST_CAMPAIGNS,
  REST_CREATIVES,
  REST_CREATIVE_SINGULAR,
  REST_CREDITS,
  EXPIRE_ON,
  YOUR_TRIAL_END,
  BILLING_AND_PAYMENTS_TITLE,
  BUTTON_GO_TO_BILLING,
  REST_VIDEOS,
  REST_VIDEO_SINGULAR,
  CREDIT_REWARD_MODAL_BUTTON_TEXT,
  STORAGE_USAGE_TITLE,
  STORAGE_USAGE_LABEL,
  MODAL_CREDITS_USAGE_DETAILED_LINK,
  BUTTON_HIRE_PERFORMANCE_SPECIALIST,
} from "../../i18n/keysTranslations";
import Tooltip from "@mui/material/Tooltip";
import Button from "../Buttons/Button";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { setShowModalSelectSubscription } from "../../actions/ui";
import { getPathDatabaseByRole } from "../../actions/auth";
import { hasRoleCapability } from "../../utils/roleCapabilities";
import {
  MAX_BYTES_STORAGE,
  // ADVERTISER_ROLE,
  SUPER_ADMIN_ROLE,
  ADS_ACTION_TYPES,
  PLAN_TYPE_CREDITS,
  IS_WHITE_LABEL_BRAND,
} from "../../utils/constants";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMaxCampaignsBySubscriptionPlan } from "../../actions/getters";
import CreditRewardModal from "../Modal/CreditRewardModal/CreditRewardModal";
import ModalCreditsUsageDetailed from "../Modal/ModalCreditsUsageDetailed";
import ModalPerformanceMarketingSpecialist from "../Modal/ModalPerformanceMarketingSpecialist";
import {
  startGetHasAddonSpendFee,
  startListenerAgencyStorage,
} from "../../actions/user";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { resolvePlanTextUsageRemaining } from "../../utils/creditActionCosts";

const PlanText = ({ variant = "h6", sxTypography = {} }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_VIDEO_ACTION } = useCreditActionCosts();
  const location = useLocation();
  const isInPaywall = location.pathname === "/paywall";

  const adsCampaignsIDsByAgency = useSelectorApp(
    (state) => state.adsCampaigns.adsCampaignsIDsByAgency
  );
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const maxCampaigns = dispatch(getMaxCampaignsBySubscriptionPlan());
  const { role, agencyID } = dispatch(getPathDatabaseByRole());
  const subscriptionPlan = useSelectorApp(
    (state) => state.payments.subscriptionPlan
  );
  const typeOfPlans = useSelectorApp((state) => state.payments.typeOfPlans);
  const isLinkedInPostShared = useSelectorApp(
    (state) => state.payments.isLinkedInPostShared
  );
  const storage = useSelectorApp((state) => state.user.storage);
  const brandUsageAllocation = useSelectorApp(
    (state) => state.payments.brandUsageAllocation
  );
  const brandUsageConsumed = useSelectorApp(
    (state) => state.payments.brandUsageConsumed
  );
  // El reward de créditos por post en LinkedIn es una promo de Adle: no aplica en white label.
  const showCreditRewardModal =
    !IS_WHITE_LABEL_BRAND &&
    typeOfPlans === PLAN_TYPE_CREDITS &&
    !isLinkedInPostShared &&
    subscriptionPlanInfo;

  const navigate = useNavigate();

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isClickedOpen, setIsClickedOpen] = useState(false);
  const [creditRewardModalOpen, setCreditRewardModalOpen] = useState(false);
  const [creditsUsageDetailedOpen, setCreditsUsageDetailedOpen] =
    useState(false);
  const [performanceSpecialistOpen, setPerformanceSpecialistOpen] =
    useState(false);
  const [hasAddonSpendFee, setHasAddonSpendFee] = useState(false);
  const finishTrialDate = subscriptionPlanInfo?.finishTrialDate;
  const now = new Date();
  const isTrialExpired = finishTrialDate
    ? new Date(finishTrialDate).getTime() < now.getTime()
    : false;

  const storageListenerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    dispatch(
      startListenerAgencyStorage((refInstance) => {
        if (!isMounted) return;
        storageListenerRef.current = refInstance;
      })
    );

    return () => {
      isMounted = false;
      if (storageListenerRef.current) {
        off(storageListenerRef.current);
        storageListenerRef.current = null;
      }
    };
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      const hasFee = await dispatch(startGetHasAddonSpendFee());
      setHasAddonSpendFee(hasFee === true);
    })();
    // eslint-disable-next-line
  }, [agencyID]);

  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;

  const usedBytes = storage?.usedBytes || 0;
  const configuredMaxBytes = storage.maxBytes || MAX_BYTES_STORAGE;
  const maxGB = configuredMaxBytes / GB;

  const formatStorage = (bytes) => {
    if (bytes < MB) {
      return { value: bytes / KB, unit: "KB" };
    }
    if (bytes < GB) {
      return { value: bytes / MB, unit: "MB" };
    }
    return { value: bytes / GB, unit: "GB" };
  };

  const { value: rawStorageValue, unit: storageUnit } =
    formatStorage(usedBytes);
  const storageValue =
    rawStorageValue >= maxGB
      ? Math.round(rawStorageValue)
      : Number(rawStorageValue.toFixed(2));

  const storagePercentage =
    usedBytes > 0 ? Math.min(100, (usedBytes / configuredMaxBytes) * 100) : 0;

  const restCampaigns = maxCampaigns - adsCampaignsIDsByAgency.length;

  const showRestCampaigns =
    subscriptionPlan ||
    (!subscriptionPlan && subscriptionPlanInfo?.benefits?.campaigns > 0);

  const showRestCreatives =
    subscriptionPlan ||
    (!subscriptionPlan && subscriptionPlanInfo?.benefits?.creatives > 0);

  const showRestVideos =
    subscriptionPlan ||
    (!subscriptionPlan && subscriptionPlanInfo?.benefits?.videos > 0);
  const showRestVideosInCreditsPlan =
    showRestVideos &&
    ADS_CAMPAIGN_GENERATE_VIDEO_ACTION?.usageKeyByPlanType?.[
      PLAN_TYPE_CREDITS
    ] === ADS_ACTION_TYPES.VIDEOS;

  const agencyRestCredits = subscriptionPlanInfo?.usage?.credits || 0;
  const agencyRestVideos = subscriptionPlanInfo?.usage?.videos || 0;

  const restCredits = useMemo(
    () =>
      resolvePlanTextUsageRemaining({
        agencyRemaining: agencyRestCredits,
        brandLimit: brandUsageAllocation?.credits,
        brandConsumed: brandUsageConsumed?.credits,
      }),
    [
      agencyRestCredits,
      brandUsageAllocation?.credits,
      brandUsageConsumed?.credits,
    ]
  );

  const restVideos = useMemo(
    () =>
      resolvePlanTextUsageRemaining({
        agencyRemaining: agencyRestVideos,
        brandLimit: brandUsageAllocation?.videos,
        brandConsumed: brandUsageConsumed?.videos,
      }),
    [agencyRestVideos, brandUsageAllocation?.videos, brandUsageConsumed?.videos]
  );

  const restCreatives = subscriptionPlanInfo?.usage?.creatives || 0;

  const getSubTextTooltip = () => {
    if (subscriptionPlan && subscriptionPlanInfo?.renewDate) {
      return t(EXPIRE_ON, {
        date: new Date(subscriptionPlanInfo?.renewDate),
      });
    }

    if (isTrialExpired) {
      return t(YOUR_TRIAL_END);
    }

    return "";
  };

  const handleTooltipOpen = () => {
    if (!isClickedOpen) {
      setTooltipOpen(true);
    }
  };

  const handleTooltipClose = () => {
    if (!isClickedOpen) {
      setTooltipOpen(false);
    }
  };

  const onCloseTooltip = () => {
    setIsClickedOpen(false);
    setTooltipOpen(false);
  };

  return (
    <>
      <Tooltip
        open={tooltipOpen || isClickedOpen}
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
        disableFocusListener
        disableTouchListener
        componentsProps={{
          tooltip: {
            "data-plan-tooltip": "true",
            sx: {
              p: 0,
              borderRadius: 3,
              mt: "8px !important",
              backgroundColor: "background.paper",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
            },
          },
        }}
        title={
          <Box
            sx={{
              p: 2,
            }}
          >
            {typeOfPlans === PLAN_TYPE_CREDITS ? (
              <>
                <Box
                  sx={{
                    mt: 1,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {t(REST_CREDITS, { value: restCredits })}
                  </Typography>
                </Box>
                {showRestVideosInCreditsPlan && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, mb: 1, color: "text.primary" }}
                  >
                    {t(restVideos === 1 ? REST_VIDEO_SINGULAR : REST_VIDEOS, {
                      value: restVideos > 0 ? restVideos : 0,
                    })}
                  </Typography>
                )}
              </>
            ) : (
              <>
                {showRestCampaigns && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, mb: 1, color: "text.primary" }}
                  >
                    {t(REST_CAMPAIGNS, {
                      value: restCampaigns > 0 ? restCampaigns : 0,
                    })}
                  </Typography>
                )}
                {showRestCreatives && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, mb: 1, color: "text.primary" }}
                  >
                    {t(
                      restCreatives === 1
                        ? REST_CREATIVE_SINGULAR
                        : REST_CREATIVES,
                      {
                        value: restCreatives > 0 ? restCreatives : 0,
                      }
                    )}
                  </Typography>
                )}
                {showRestVideos && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, mb: 1, color: "text.primary" }}
                  >
                    {t(restVideos === 1 ? REST_VIDEO_SINGULAR : REST_VIDEOS, {
                      value: restVideos > 0 ? restVideos : 0,
                    })}
                  </Typography>
                )}
              </>
            )}

            {role === SUPER_ADMIN_ROLE && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  mb: 1,
                  color: "text.primary",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  onCloseTooltip();
                  setCreditsUsageDetailedOpen(true);
                }}
              >
                {t(MODAL_CREDITS_USAGE_DETAILED_LINK)}
              </Typography>
            )}

            <Typography variant="body2" sx={{ color: "text.primary", mt: 2.5 }}>
              {getSubTextTooltip()}
            </Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, color: "text.primary" }}
              >
                {t(STORAGE_USAGE_TITLE)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: "text.primary" }}>
                {t(STORAGE_USAGE_LABEL, {
                  value: storageValue,
                  unit: storageUnit,
                  maxValue: Number(maxGB.toFixed(2)),
                })}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={storagePercentage}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                  },
                }}
              />
            </Box>

            {hasRoleCapability(role, "canEditBilling") && !isInPaywall && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Button
                  onClick={() => {
                    onCloseTooltip();
                    dispatch(setShowModalSelectSubscription(true));
                  }}
                  color="primary"
                  size="small"
                  fullWidth
                >
                  {t(
                    subscriptionPlan
                      ? BUTTON_UPGRADE_PLAN_2
                      : BUTTON_UPGRADE_TO_PAID_PLAN
                  )}
                </Button>
                {subscriptionPlan && !hasAddonSpendFee && (
                  <Button
                    color="secondary"
                    fullWidth
                    size="small"
                    onClick={() => {
                      onCloseTooltip();
                      setPerformanceSpecialistOpen(true);
                    }}
                  >
                    {t(BUTTON_HIRE_PERFORMANCE_SPECIALIST)}
                  </Button>
                )}
                <Button
                  color="secondary"
                  fullWidth
                  size="small"
                  onClick={() => {
                    onCloseTooltip();
                    navigate("/billing");
                  }}
                >
                  {t(BUTTON_GO_TO_BILLING)}
                </Button>
                {showCreditRewardModal && (
                  <Button
                    color="secondary"
                    fullWidth
                    size="small"
                    onClick={() => {
                      onCloseTooltip();
                      setCreditRewardModalOpen(true);
                    }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        fontWeight: 600,
                        fontSize: "13px",
                        textTransform: "none",
                      }}
                    >
                      <Trans
                        i18nKey={CREDIT_REWARD_MODAL_BUTTON_TEXT}
                        components={{
                          u: <u />,
                          em: <em />,
                        }}
                      />
                    </Typography>
                  </Button>
                )}
              </Box>
            )}
          </Box>
        }
      >
        <Box
          data-plan-tooltip-anchor="true"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            cursor: "pointer",
          }}
        >
          <Typography
            variant={variant}
            sx={{
              mb: -0.5,
              color: "text.primary",
              ...sxTypography,
            }}
          >
            {t(BILLING_AND_PAYMENTS_TITLE)}
          </Typography>
        </Box>
      </Tooltip>

      {creditRewardModalOpen && (
        <CreditRewardModal
          modalOpen={creditRewardModalOpen}
          onCloseModal={() => setCreditRewardModalOpen(false)}
        />
      )}
      {creditsUsageDetailedOpen && (
        <ModalCreditsUsageDetailed
          modalOpen={creditsUsageDetailedOpen}
          onCloseModal={() => setCreditsUsageDetailedOpen(false)}
        />
      )}
      {performanceSpecialistOpen && (
        <ModalPerformanceMarketingSpecialist
          modalOpen={performanceSpecialistOpen}
          onCloseModal={() => setPerformanceSpecialistOpen(false)}
        />
      )}
    </>
  );
};

export default PlanText;
